import { Box, Typography, Paper, Button, List, ListItem, ListItemIcon, ListItemText, Chip, Divider, Stack, SxProps, Theme, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ExternalUrls } from '../../lib/constants';
import { usePostHog } from '../widgets/PostHogProvider';

interface PricingCardProps {
  // Header
  overhead?: string;
  title: string;
  caption?: string;

  // Highlighting
  highlighted?: boolean;
  highlightText?: string;

  // Price Area
  price: string;
  priceSuffix?: string;
  billingSlot?: React.ReactNode;
  savings?: string;
  priceNote?: string;

  // Content
  description?: string;
  features: readonly (string | { text: string; tooltip?: string })[];
  excludedFeatures?: readonly string[];

  // CTA
  ctaText?: string;
  ctaHref?: string;
  ctaAction?: () => void;
  ctaSubText?: string;
  ctaVariant?: 'contained' | 'outlined' | 'text';

  // Styling
  themeColor?: string; // Override primary color (e.g., 'audience.individual.main')
  sx?: SxProps<Theme>;
}

function SavingsChip({ savings }: { savings?: string }) {
  if (!savings) return null;
  return (
    <Chip
      label={`Save ${savings}`}
      size="small"
      sx={{
        bgcolor: '#22C55E', // Tailwind green-500
        color: '#ffffff',
        fontWeight: 700,
        height: 24,
        fontSize: '0.75rem',
      }}
    />
  );
}

function ExcludedFeatures({ features }: { features?: readonly string[] }) {
  if (!features || features.length === 0) return null;
  return (
    <List dense sx={{ opacity: 0.6, mt: 0, pt: 0 }}>
      {features.map((feature, i) => (
        <ListItem key={i} sx={{ px: 0 }} alignItems="flex-start">
          <ListItemIcon sx={{ minWidth: 32, mt: 1 }}>
            <CancelIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary={feature}
            primaryTypographyProps={{
              variant: 'body2',
              sx: { textDecoration: 'line-through' }
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

export function PricingCard({
  overhead,
  title,
  caption,
  price,
  priceSuffix,
  billingSlot,
  savings,
  priceNote,
  description,
  features,
  excludedFeatures,
  highlighted = false,
  highlightText = 'Recommended',
  ctaText = 'Get Started',
  ctaHref,
  ctaAction,
  ctaSubText,
  ctaVariant,
  themeColor = 'primary.main',
  sx,
}: PricingCardProps) {
  const { capture } = usePostHog();

  // Default variant logic if not explicitly provided
  const buttonVariant = ctaVariant || (highlighted ? 'contained' : 'outlined');
  const href = ctaHref || (!ctaAction ? ExternalUrls.dashboardSignup : undefined);

  const handleCtaClick = () => {
    capture('pricing_cta_clicked', {
      plan_title: title,
      plan_overhead: overhead,
      plan_price: price,
      plan_price_suffix: priceSuffix,
      cta_text: ctaText,
      cta_href: href,
      is_highlighted: highlighted,
    });
    ctaAction?.();
  };

  return (
    <Paper
      elevation={highlighted ? 4 : 0}
      sx={[
        {
          p: 4,
          height: '100%',
          borderRadius: 3,
          border: '2px solid',
          borderColor: highlighted ? themeColor : 'divider',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s',
          bgcolor: highlighted ? 'background.paper' : 'transparent', // White for highlighted, transparent for others
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.1)',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {highlighted && (
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: themeColor,
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontSize: '0.875rem', // Increased size
            fontWeight: 700,
            zIndex: 2,
          }}
        >
          {highlightText}
        </Box>
      )}

      {overhead && (
        <Typography
          variant="overline"
          fontWeight={700}
          sx={{ mb: 0.5, display: 'block', fontSize: '0.85rem', letterSpacing: 1, color: highlighted ? themeColor : 'text.secondary' }}
        >
          {overhead}
        </Typography>
      )}

      <Typography variant="h3" component="h3" sx={{ mb: 0.5, fontSize: '2rem', fontWeight: 700 }}>
        {title}
      </Typography>

      {caption && (
        <Typography variant="caption" fontStyle="italic" color="text.secondary" sx={{ mb: 2, display: 'block', fontSize: '0.8rem' }}>
          {caption}
        </Typography>
      )}

      <Box sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Typography
            component="span"
            sx={{
              fontSize: '2.75rem', // Slightly larger
              fontWeight: 800,
              color: themeColor,
              lineHeight: 1,
              minWidth: '140px', // Prevent layout shift when price length changes
              display: 'inline-block'
            }}
          >
            {price}
          </Typography>

          {/* Billing slot and chips stack */}
          {(billingSlot || savings) && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 'fit-content' }}>
              {billingSlot}
              <SavingsChip savings={savings} />
            </Stack>
          )}
        </Stack>

        {priceSuffix && (
          <Typography component="span" variant="body1" color="text.secondary" fontWeight={500} sx={{ display: 'block' }}>
            {priceSuffix}
          </Typography>
        )}

        {priceNote && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {priceNote}
          </Typography>
        )}
      </Box>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 48 }}>
          {description}
        </Typography>
      )}

      <Divider sx={{ mb: 3 }} />

      <List dense sx={{ flex: 1, mb: 0 }}>
        {features.map((featureItem, index) => {
          const isObject = typeof featureItem !== 'string';
          const featureText = isObject ? featureItem.text : featureItem;
          const tooltip = isObject ? featureItem.tooltip : undefined;

          return (
            <ListItem key={index} sx={{ px: 0 }} alignItems="flex-start">
              <ListItemIcon sx={{ minWidth: 32, mt: 1 }}>
                <CheckCircleIcon sx={{ color: themeColor, fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box component="div" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <span>
                      {featureText.split(/(<b>.*?<\/b>)/).map((part, i) => {
                        if (part.startsWith('<b>') && part.endsWith('</b>')) {
                          return (
                            <Box component="span" key={i} fontWeight={700}>
                              {part.replace(/<\/?b>/g, '')}
                            </Box>
                          );
                        }
                        return <span key={i}>{part}</span>;
                      })}
                    </span>
                    {tooltip && (
                      <Tooltip title={tooltip} arrow placement="top">
                        <HelpOutlineIcon sx={{ fontSize: '1rem', color: 'text.disabled', ml: 0.5, mt: 0.5, cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                primaryTypographyProps={{ variant: 'body1', color: 'text.secondary', component: 'div' }}
              />
            </ListItem>
          );
        })}
      </List>

      <ExcludedFeatures features={excludedFeatures} />

      <Box sx={{ mt: 'auto', pt: 3 }}>
        <Button
          variant={buttonVariant}
          fullWidth
          onClick={handleCtaClick}
          href={ctaAction ? undefined : href}
          size="large"
          sx={{ mb: 1, py: 1.5, fontWeight: 700 }}
        >
          {ctaText}
        </Button>
        {ctaSubText && (
          <Typography variant="caption" align="center" sx={{ display: 'block', color: 'text.secondary' }}>
            {ctaSubText}
          </Typography>
        )}
        {/* Placeholder for alignment if subtext is missing but needed for grid alignment? 
            Ideally parent handles alignment, or we pass a prop to force space. 
            For now, simple is better. 
        */}
      </Box>
    </Paper>
  );
}
